import { LoadingButton } from "@mui/lab";
import {
    Alert,
    Box,
    Button,
    Collapse,
    Divider,
    FormControl,
    Icon,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
} from "@mui/material";
import { useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AccessDto } from "../../../api/psc";
import { AppDatePicker, MultiSelect } from "../../../components/exports";
import SectionHeader from "../../../components/shared/SectionHeader";
import SmsCampaignRangeSelect from "../../../components/shared/SmsCampaignRangeSelect";
import {
    floorRange,
    groundAreaRange,
    residentialAreaRange,
    subFloorRange,
    unitRange,
} from "../../../constants/ProjectConstants";
import { DialogContext } from "../../../contexts/DialogContext";
import { RootState } from "../../../slices/Store";
import { ProjectFilterHelper } from "../../../utils/filter";
import { ValidateSmsCampaignProjectFilter } from "../../../validations/SmsCampaignValidation";
import SmsCampaignFilterCalculationResult from "../dialogs/SmsCampaignFilterCalculationResult";
import {
    setSmsCampaignProjectFilter,
    smsCampaignNextStep,
    smsCampaignPrevStep,
} from "../smsCampaignSlice/SmsCampaignSlice";

// updated
export default function SmsCampaignProjectDestination() {
    const dispatch = useDispatch();

    const { openDialog } = useContext(DialogContext);

    const [error, setError] = useState<string | null>(null);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    const smsCampaign = useSelector((state: RootState) => state.smsCampaign);
    const { accessList, baseInfo: projectRelatedEntities } = useSelector(
        (state: RootState) => state.shared
    );

    // -----------------------------------------------------------------------------------------------------//
    // -----------------------------------  filter change handlers -----------------------------------------//
    // -----------------------------------------------------------------------------------------------------//

    const handleSelectedAccessChange = (access: AccessDto) => {
        const filter = {
            ...smsCampaign.projectFilter,
            targetAccessId: access.id,
            cityIds: [],
            regionIds: [],
        };
        dispatch(setSmsCampaignProjectFilter(filter));
    };

    const handleCiyChange = (values: number[]) => {
        const filter = {
            ...smsCampaign.projectFilter,
            cityIds: values,
            regionIds: [],
            subRegionIds: [],
        };
        dispatch(setSmsCampaignProjectFilter(filter));
    };

    const handleRegionChange = (values: number[]) => {
        const filter = {
            ...smsCampaign.projectFilter,
            regionIds: values,
            subRegionIds: [],
        };
        dispatch(setSmsCampaignProjectFilter(filter));
    };

    const handleSubRegionChange = (values: number[]) => {
        const filter = {
            ...smsCampaign.projectFilter,
            subRegionIds: values,
        };
        dispatch(setSmsCampaignProjectFilter(filter));
    };

    const handlePhaseChange = (values: number[]) => {
        const filter = {
            ...smsCampaign.projectFilter,
            phaseIds: values,
        };
        dispatch(setSmsCampaignProjectFilter(filter));
    };

    const handleUpdateDateFromChange = (date: string) => {
        const filter = {
            ...smsCampaign.projectFilter,
            updateDateFrom: date,
        };
        dispatch(setSmsCampaignProjectFilter(filter));
    };

    const handleUpdateDateToChange = (date: string) => {
        const filter = {
            ...smsCampaign.projectFilter,
            updateDateTo: date,
        };
        dispatch(setSmsCampaignProjectFilter(filter));
    };

    const handleStructureTypeChange = (values: number[]) => {
        const filter = {
            ...smsCampaign.projectFilter,
            structureTypeIds: values,
        };
        dispatch(setSmsCampaignProjectFilter(filter));
    };

    const handleUsageTypeChange = (values: number[]) => {
        const filter = { ...smsCampaign.projectFilter, usageTypesIds: values };
        dispatch(setSmsCampaignProjectFilter(filter));
    };

    const handleFloorChange = (from?: number, to?: number) => {
        const filter = {
            ...smsCampaign.projectFilter,
            floorFrom: from,
            floorTo: to,
        };
        dispatch(setSmsCampaignProjectFilter(filter));
    };

    const handleUnitChange = (from?: number, to?: number) => {
        const filter = {
            ...smsCampaign.projectFilter,
            unitFrom: from,
            unitTo: to,
        };
        dispatch(setSmsCampaignProjectFilter(filter));
    };

    const handleSubFloorChange = (from?: number, to?: number) => {
        const filter = {
            ...smsCampaign.projectFilter,
            subFloorFrom: from,
            subFloorTo: to,
        };
        dispatch(setSmsCampaignProjectFilter(filter));
    };

    const handleGroundAreaChange = (from?: number, to?: number) => {
        const filter = {
            ...smsCampaign.projectFilter,
            groundAreaFrom: from,
            groundAreaTo: to,
        };
        dispatch(setSmsCampaignProjectFilter(filter));
    };

    const handleResidentialAreaChange = (from?: number, to?: number) => {
        const filter = {
            ...smsCampaign.projectFilter,
            residentialAreaFrom: from,
            residentialAreaTo: to,
        };
        dispatch(setSmsCampaignProjectFilter(filter));
    };

    const toggleAdvancedFilters = () => {
        setShowAdvancedFilters(!showAdvancedFilters);
    };

    const openSmsCalculateDialog = () => {
        openDialog(<SmsCampaignFilterCalculationResult />);
    };

    // -----------------------------------------------------------------------------------------------------//
    // ---------------------------------  filter change handlers end ---------------------------------------//
    // -----------------------------------------------------------------------------------------------------//

    // -----------------------------------------------------------------------------------------------------//
    // ------------------------------------  step change handlers ------------------------------------------//
    // -----------------------------------------------------------------------------------------------------//
    const handlePrevious = () => {
        dispatch(smsCampaignPrevStep());
    };

    const handleNext = () => {
        const errorMessage = ValidateSmsCampaignProjectFilter(smsCampaign);

        if (errorMessage) {
            setError(errorMessage);
            return;
        }

        dispatch(smsCampaignNextStep());
    };
    // -----------------------------------------------------------------------------------------------------//
    // ----------------------------------  step change handlers end ----------------------------------------//
    // -----------------------------------------------------------------------------------------------------//

    // waiting for default filter to be created
    if (!smsCampaign.projectFilter) {
        return <></>;
    }

    // -----------------------------------------------------------------------------------------------------//
    // ----------------------------------------  business logic --------------------------------------------//
    // -----------------------------------------------------------------------------------------------------//

    const availableAccessList = accessList.filter(
        (a) => a.canBeUsedForSmsFiltering
    );

    const selectedAccess = !!smsCampaign.projectFilter.targetAccessId
        ? availableAccessList.find(
              (a) => a.id === smsCampaign.projectFilter.targetAccessId
          )
        : null;

    const projectFilterHelper = new ProjectFilterHelper(
        projectRelatedEntities,
        accessList,
        smsCampaign.projectFilter,
        selectedAccess
    );

    const availableCities = projectFilterHelper.getCities();
    const availableRegions = projectFilterHelper.getRegions();
    const availableSubRegions = projectFilterHelper.getSubRegions();
    const availablePhases = projectFilterHelper.getPhases();

    // -----------------------------------------------------------------------------------------------------//
    // -------------------------------------  business logic end -------------------------------------------//
    // -----------------------------------------------------------------------------------------------------//

    return (
        <Stack spacing={2}>
            <SectionHeader title="فیلتر پروژه ها" />

            <Alert severity="info">
                در این بخش میتوانید فیلتر مورد نظر برای پروژه ها را انتخاب کرده
                تا پیامک برای سازندگان این پروژه ها ارسال شود.
            </Alert>

            <FormControl fullWidth>
                <InputLabel>نوع دسترسی</InputLabel>

                <Select
                    label="نوع دسترسی"
                    value={smsCampaign.projectFilter.targetAccessId ?? ""}
                >
                    {availableAccessList.map((a) => {
                        return (
                            <MenuItem
                                key={a.id}
                                onClick={() => handleSelectedAccessChange(a)}
                                value={a.id}
                            >
                                {a.title}
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>

            {selectedAccess && (
                <>
                    {availableCities.length > 1 && (
                        <MultiSelect
                            label="شهر"
                            values={availableCities}
                            selectedIds={smsCampaign.projectFilter.cityIds}
                            onValueChange={handleCiyChange}
                        />
                    )}

                    <MultiSelect
                        label="منطقه"
                        values={availableRegions}
                        selectedIds={smsCampaign.projectFilter.regionIds}
                        onValueChange={handleRegionChange}
                    />

                    {smsCampaign.projectFilter.regionIds.length > 0 &&
                        availableSubRegions.length > 0 && (
                            <MultiSelect
                                label="محله"
                                values={availableSubRegions}
                                selectedIds={
                                    smsCampaign.projectFilter.subRegionIds
                                }
                                onValueChange={handleSubRegionChange}
                            />
                        )}

                    <MultiSelect
                        label="مرحله"
                        values={availablePhases}
                        selectedIds={smsCampaign.projectFilter.phaseIds}
                        onValueChange={handlePhaseChange}
                    />

                    <Box>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Typography>نمایش فیلتر های پیشرفته</Typography>

                            <IconButton onClick={toggleAdvancedFilters}>
                                {!showAdvancedFilters ? (
                                    <Icon>expand_more</Icon>
                                ) : (
                                    <Icon>expand_less</Icon>
                                )}
                            </IconButton>
                        </Stack>

                        <Collapse in={showAdvancedFilters}>
                            <Stack spacing={2} sx={{ mt: 2 }}>
                                <MultiSelect
                                    label="نوع سازه"
                                    values={
                                        projectRelatedEntities.structureTypes
                                    }
                                    selectedIds={
                                        smsCampaign.projectFilter
                                            .structureTypeIds
                                    }
                                    onValueChange={handleStructureTypeChange}
                                />

                                <MultiSelect
                                    label="نوع کاربری"
                                    values={projectRelatedEntities.usageTypes}
                                    selectedIds={
                                        smsCampaign.projectFilter.usageTypesIds
                                    }
                                    onValueChange={handleUsageTypeChange}
                                />

                                <Box>
                                    <InputLabel>تعداد طبقه</InputLabel>
                                    <SmsCampaignRangeSelect
                                        from={
                                            smsCampaign.projectFilter.floorFrom
                                        }
                                        to={smsCampaign.projectFilter.floorTo}
                                        onValueChange={handleFloorChange}
                                        valueProvider={floorRange}
                                    />
                                </Box>

                                <Box>
                                    <InputLabel>تعداد واحد</InputLabel>
                                    <SmsCampaignRangeSelect
                                        from={
                                            smsCampaign.projectFilter.unitFrom
                                        }
                                        to={smsCampaign.projectFilter.unitTo}
                                        onValueChange={handleUnitChange}
                                        valueProvider={unitRange}
                                    />
                                </Box>

                                <Box>
                                    <InputLabel>طبقات منفی</InputLabel>
                                    <SmsCampaignRangeSelect
                                        from={
                                            smsCampaign.projectFilter
                                                .subFloorFrom
                                        }
                                        to={
                                            smsCampaign.projectFilter.subFloorTo
                                        }
                                        onValueChange={handleSubFloorChange}
                                        valueProvider={subFloorRange}
                                    />
                                </Box>

                                <Box>
                                    <InputLabel>متراژ زمین</InputLabel>
                                    <SmsCampaignRangeSelect
                                        from={
                                            smsCampaign.projectFilter
                                                .groundAreaFrom
                                        }
                                        to={
                                            smsCampaign.projectFilter
                                                .groundAreaTo
                                        }
                                        onValueChange={handleGroundAreaChange}
                                        valueProvider={groundAreaRange}
                                    />
                                </Box>

                                <Box>
                                    <InputLabel>زیربنای مفید</InputLabel>
                                    <SmsCampaignRangeSelect
                                        from={
                                            smsCampaign.projectFilter
                                                .groundAreaFrom
                                        }
                                        to={
                                            smsCampaign.projectFilter
                                                .groundAreaTo
                                        }
                                        onValueChange={
                                            handleResidentialAreaChange
                                        }
                                        valueProvider={residentialAreaRange}
                                    />
                                </Box>

                                <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent="space-between"
                                >
                                    <AppDatePicker
                                        label="به روز رسانی از"
                                        onChange={handleUpdateDateFromChange}
                                        initialValue={
                                            smsCampaign.projectFilter
                                                .updateDateFrom ?? null
                                        }
                                    />

                                    <AppDatePicker
                                        label="به روز رسانی تا"
                                        onChange={handleUpdateDateToChange}
                                        initialValue={
                                            smsCampaign.projectFilter
                                                .updateDateTo ?? null
                                        }
                                    />
                                </Stack>
                            </Stack>
                        </Collapse>
                    </Box>

                    <Divider />

                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Typography sx={{ flexGrow: 1 }} color="text.secondary">
                            محاسبه تعداد پارت پیامکی برای فیلتر انتحاب شده
                        </Typography>

                        <Button
                            variant="outlined"
                            onClick={openSmsCalculateDialog}
                            endIcon={<Icon>calculate</Icon>}
                        >
                            محاسبه
                        </Button>
                    </Stack>
                </>
            )}

            <Divider />

            {error && <Alert severity="error">{error}</Alert>}

            <Stack direction="row" justifyContent="space-between">
                <Button
                    onClick={handlePrevious}
                    variant="outlined"
                    startIcon={<Icon>navigate_next</Icon>}
                >
                    مرحله قبل
                </Button>

                <LoadingButton
                    onClick={handleNext}
                    variant="contained"
                    color="primary"
                    endIcon={<Icon>navigate_before</Icon>}
                >
                    مرحله بعد
                </LoadingButton>
            </Stack>
        </Stack>
    );
}
